import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BadgeCheck,
  Building2,
  Check,
  ChevronLeft,
  CircleAlert,
  Clock3,
  Download,
  FileCheck2,
  FileText,
  Inbox,
  LoaderCircle,
  LogOut,
  RefreshCw,
  ShieldCheck,
  UserRoundCheck,
  X,
} from "lucide-react";
import { ApiFailure, api } from "./api.js";
import type { MysqlMe } from "./auth-types.js";
import { BrandMark } from "./BrandMark.js";

type ReviewCaseStatus = "SUBMITTED" | "UNDER_REVIEW" | "PENDING_RECHECK" | "APPROVED" | "REJECTED" | "CANCELLED";
type ReviewCaseType = "PERSONAL" | "ORGANIZATION";

interface ReviewMaterial {
  id: string;
  materialType: string;
  fileName: string;
  detectedMediaType: string;
  byteSize: number;
  plaintextDigest: string;
  scannerAssurance: string;
  createdAt: string;
}

interface ReviewCase {
  id: string;
  caseType: ReviewCaseType;
  status: ReviewCaseStatus;
  priority: "NORMAL" | "HIGH";
  reviewPolicy: "LEGACY_SINGLE" | "DUAL_REVIEW";
  reviewCycle: number;
  recheckStatus: "NOT_REQUIRED" | "PENDING" | "CLAIMED" | "RETURNED" | "CONFIRMED";
  slaState: "ON_TRACK" | "WARNING" | "OVERDUE" | "COMPLETE";
  slaDueAt: string | null;
  version: number;
  applicantMasked: string;
  subjectLabel: string;
  applicationNo: string | null;
  assignedToMe: boolean;
  assignedReviewerAccountId: string | null;
  submittedAt: string;
  claimedAt: string | null;
  decidedAt: string | null;
  materialCount: number;
  materialRequirements: string[];
  materialRequirementsMet: boolean;
}

interface ReviewerDirectoryEntry {
  accountId: string;
  maskedEmail: string;
  roles: string[];
}
interface ReviewTimelineEvent {
  id: string;
  category: "ASSIGNMENT" | "RECOMMENDATION" | "RECHECK";
  eventType: string;
  reviewCycle: number;
  actorAccountId: string | null;
  reasonCode: string | null;
  occurredAt: string;
}

interface AppealView {
  id: string;
  reviewCaseId: string;
  appealedReviewCycle: number;
  status: "SUBMITTED" | "UNDER_REVIEW" | "ACCEPTED" | "REJECTED" | "WITHDRAWN";
  version: number;
  assignedReviewerAccountId: string | null;
  submittedAt: string;
  decidedAt: string | null;
  canCurrentActorClaim: boolean;
  canCurrentActorDecide: boolean;
}

interface BusinessCalendar {
  id: string; code: string; name: string; timeZone: string; workdayStart: string; workdayEnd: string; version: number;
  days: Array<{ date: string; dayType: "WORKDAY" | "HOLIDAY"; name: string }>;
}

interface MfaResetRequest {
  id: string; targetAccountId: string;
  status: "SUBMITTED" | "IDENTITY_VERIFIED" | "PENDING_APPROVAL" | "APPROVED" | "COOLING" | "REJECTED" | "CANCELLED" | "EXPIRED" | "APPLIED";
  version: number; requestedAt: string; identityReviewedByAccountId: string | null;
  canCurrentActorVerifyIdentity: boolean;
}

interface ReviewCaseDetail extends ReviewCase {
  submittedByAccountId: string;
  personal: null | {
    identityId: string;
    realName: string;
    documentType: string;
    documentNumber: string;
  };
  organization: null | {
    organizationId: string;
    legalName: string;
    unifiedSocialCreditCode: string;
    applicantAuthority: string;
    intendedUsage: string;
  };
  materials: ReviewMaterial[];
}

function shortId(value: string, head = 7): string {
  return value.length > head * 2 + 1 ? `${value.slice(0, head)}…${value.slice(-head)}` : value;
}

function formatTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function statusName(status: ReviewCaseStatus): string {
  return {
    SUBMITTED: "待领取",
    UNDER_REVIEW: "审核中",
    PENDING_RECHECK: "待独立复核",
    APPROVED: "已通过",
    REJECTED: "已驳回",
    CANCELLED: "已取消",
  }[status];
}

function materialName(type: string): string {
  return {
    IDENTITY_DOCUMENT: "身份证明",
    LIVENESS_RESULT: "活体核验结果",
    BUSINESS_LICENSE: "营业执照或登记材料",
    AUTHORIZATION_LETTER: "授权委托材料",
    PROVIDER_RESULT: "权威服务商核验结果",
    OTHER: "其他材料",
  }[type] ?? type;
}

function ErrorNotice({ error }: { error: ApiFailure }): React.JSX.Element {
  return <div className="error-notice" role="alert"><CircleAlert /><span><b>{error.message}</b><small>{error.code}{error.requestId ? ` · 请求 ${shortId(error.requestId, 5)}` : ""}</small></span></div>;
}

export function ReviewWorkbench({
  me,
  onBack,
  onOpenEvidence,
  onOpenSecurity,
  onLogout,
}: {
  me: MysqlMe;
  onBack: () => void;
  onOpenEvidence: () => void;
  onOpenSecurity: () => void;
  onLogout: () => void;
}): React.JSX.Element {
  const [cases, setCases] = useState<ReviewCase[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const [detail, setDetail] = useState<ReviewCaseDetail | null>(null);
  const [statusFilter, setStatusFilter] = useState<ReviewCaseStatus | "ALL">("ALL");
  const [typeFilter, setTypeFilter] = useState<ReviewCaseType | "ALL">("ALL");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ApiFailure | null>(null);
  const [success, setSuccess] = useState("");
  const [reasonCode, setReasonCode] = useState("MATERIAL_INVALID");
  const [reason, setReason] = useState("");
  const [decisionKey, setDecisionKey] = useState(() => crypto.randomUUID());
  const [reviewers, setReviewers] = useState<ReviewerDirectoryEntry[]>([]);
  const [targetReviewerId, setTargetReviewerId] = useState("");
  const [timeline, setTimeline] = useState<ReviewTimelineEvent[]>([]);
  const [appeals, setAppeals] = useState<AppealView[]>([]);
  const [calendar, setCalendar] = useState<BusinessCalendar | null>(null);
  const [mfaResets, setMfaResets] = useState<MfaResetRequest[]>([]);
  const [calendarDate, setCalendarDate] = useState("");
  const [calendarDayType, setCalendarDayType] = useState<"WORKDAY" | "HOLIDAY">("HOLIDAY");
  const [calendarDayName, setCalendarDayName] = useState("");
  const [workdayStart, setWorkdayStart] = useState("09:00");
  const [workdayEnd, setWorkdayEnd] = useState("18:00");

  const loadDetail = useCallback(async (caseId: string): Promise<void> => {
    const [response, timelineResponse] = await Promise.all([
      api<ReviewCaseDetail>(`/v2/review/cases/${caseId}`),
      api<ReviewTimelineEvent[]>(`/v2/review/cases/${caseId}/timeline`),
    ]);
    setDetail(response.data);
    setTimeline(timelineResponse.data);
  }, []);

  const loadCases = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({ limit: "200" });
      if (statusFilter !== "ALL") query.set("status", statusFilter);
      if (typeFilter !== "ALL") query.set("caseType", typeFilter);
      const response = await api<ReviewCase[]>(`/v2/review/cases?${query}`);
      setCases(response.data);
      const currentId = selectedIdRef.current;
      const nextId = currentId && response.data.some((item) => item.id === currentId)
        ? currentId
        : null;
      selectedIdRef.current = nextId;
      setSelectedId(nextId);
      if (nextId) await loadDetail(nextId);
      else { setDetail(null); setTimeline([]); }
    } catch (cause) {
      const failure = cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法加载审核队列。");
      if (failure.status === 401) onLogout();
      else setError(failure);
    } finally {
      setLoading(false);
    }
  }, [loadDetail, onLogout, statusFilter, typeFilter]);

  useEffect(() => { void loadCases(); }, [loadCases]);
  const loadAppeals = useCallback(async (): Promise<void> => {
    try { setAppeals((await api<AppealView[]>("/v2/review/appeals")).data); } catch { setAppeals([]); }
  }, []);
  useEffect(() => { void loadAppeals(); }, [loadAppeals]);
  const loadMfaResets = useCallback(async (): Promise<void> => {
    try { setMfaResets((await api<MfaResetRequest[]>("/v2/platform/mfa-reset-requests")).data); } catch { setMfaResets([]); }
  }, []);
  useEffect(() => { void loadMfaResets(); }, [loadMfaResets]);
  const loadCalendar = useCallback(async (): Promise<void> => {
    try {
      const value = (await api<BusinessCalendar>("/v2/review/business-calendar")).data;
      setCalendar(value); setWorkdayStart(value.workdayStart); setWorkdayEnd(value.workdayEnd);
    } catch { setCalendar(null); }
  }, []);
  useEffect(() => { void loadCalendar(); }, [loadCalendar]);
  useEffect(() => {
    if (!me.platformRoles.includes("PLATFORM_REVIEW_ADMIN")) return;
    void api<ReviewerDirectoryEntry[]>("/v2/review/reviewers")
      .then((response) => setReviewers(response.data))
      .catch(() => setReviewers([]));
  }, [me.platformRoles]);

  const counts = useMemo(() => ({
    total: cases.length,
    unclaimed: cases.filter((item) => item.status === "SUBMITTED").length,
    mine: cases.filter((item) => item.status === "UNDER_REVIEW" && item.assignedToMe).length,
    recheck: cases.filter((item) => item.status === "PENDING_RECHECK").length,
    overdue: cases.filter((item) => item.slaState === "OVERDUE").length,
  }), [cases]);

  const choose = async (caseId: string): Promise<void> => {
    selectedIdRef.current = caseId;
    setSelectedId(caseId);
    setBusy("detail");
    setError(null);
    setSuccess("");
    try {
      await loadDetail(caseId);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法读取审核详情。"));
    } finally {
      setBusy(null);
    }
  };

  const claim = async (): Promise<void> => {
    if (!detail) return;
    setBusy("claim");
    setError(null);
    setSuccess("");
    try {
      await api<ReviewCase>(`/v2/review/cases/${detail.id}/claim`, { method: "POST" });
      setSuccess("案件已领取；后续决定将在单个 MySQL 事务中落库。");
      await loadCases(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法领取案件。"));
    } finally {
      setBusy(null);
    }
  };

  const decide = async (decision: "APPROVE" | "REJECT"): Promise<void> => {
    if (!detail) return;
    setBusy(decision);
    setError(null);
    setSuccess("");
    try {
      const dual = detail.reviewPolicy === "DUAL_REVIEW";
      await api<ReviewCase>(`/v2/review/cases/${detail.id}/${dual ? "recommendation" : "decision"}`, {
        method: "POST",
        headers: { "idempotency-key": decisionKey },
        body: JSON.stringify({
          ...(dual ? { recommendation: decision, expectedVersion: detail.version } : { decision }),
          ...(decision === "REJECT" ? { reasonCode, reason } : {}),
        }),
      });
      setSuccess(dual
        ? "初审建议已留痕，案件进入独立复核；当前不会开通任何业务权。"
        : decision === "APPROVE" ? "历史单审案件已原子完成。" : "历史单审案件已驳回。");
      setReason("");
      setDecisionKey(crypto.randomUUID());
      await loadCases(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法提交审核决定。"));
    } finally {
      setBusy(null);
    }
  };

  const recheck = async (decision: "CONFIRM" | "RETURN_FOR_REVIEW"): Promise<void> => {
    if (!detail) return;
    setBusy(decision);
    setError(null);
    setSuccess("");
    try {
      await api<ReviewCase>(`/v2/review/cases/${detail.id}/recheck/decision`, {
        method: "POST", headers: { "idempotency-key": decisionKey },
        body: JSON.stringify({ decision, expectedVersion: detail.version,
          ...(decision === "RETURN_FOR_REVIEW" ? { reasonCode, reason } : {}) }),
      });
      setSuccess(decision === "CONFIRM" ? "独立复核已确认，最终身份状态与 Tenant 权限已在同一事务生效。" : "案件已进入新审核周期，原建议与复核事实保留。");
      setReason(""); setDecisionKey(crypto.randomUUID()); await loadCases(true);
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法提交独立复核。")); }
    finally { setBusy(null); }
  };

  const claimRecheck = async (): Promise<void> => {
    if (!detail) return;
    setBusy("claim-recheck"); setError(null); setSuccess("");
    try {
      await api<ReviewCase>(`/v2/review/cases/${detail.id}/recheck/claim`, {
        method: "POST", body: JSON.stringify({ expectedVersion: detail.version }),
      });
      setSuccess("独立复核任务已领取，复核人分离规则已在服务端校验。");
      await loadCases(true);
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法领取复核任务。")); }
    finally { setBusy(null); }
  };

  const releaseOrReassign = async (mode: "release" | "reassign"): Promise<void> => {
    if (!detail) return;
    setBusy(mode); setError(null); setSuccess("");
    try {
      await api<ReviewCase>(`/v2/review/cases/${detail.id}/${mode}`, { method: "POST", body: JSON.stringify({
        expectedVersion: detail.version, reasonCode, reason,
        ...(mode === "reassign" ? { targetReviewerAccountId: targetReviewerId } : {}),
      }) });
      setSuccess(mode === "reassign" ? "案件已原子转派，旧经办人已失去决定权。" : "案件已退回待领取队列。");
      setReason(""); await loadCases(true);
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法完成经办变更。")); }
    finally { setBusy(null); }
  };

  const claimAppeal = async (appeal: AppealView): Promise<void> => {
    setBusy(`appeal-claim:${appeal.id}`); setError(null); setSuccess("");
    try {
      await api<AppealView>(`/v2/review/appeals/${appeal.id}/claim`, { method: "POST", body: JSON.stringify({ expectedVersion: appeal.version }) });
      setSuccess("申诉已领取；原申请人、原初审员和原复核员已被排除。"); await loadAppeals();
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法领取申诉。")); }
    finally { setBusy(null); }
  };

  const decideAppeal = async (appeal: AppealView, decision: "ACCEPT" | "REJECT"): Promise<void> => {
    setBusy(`appeal-${decision}:${appeal.id}`); setError(null); setSuccess("");
    try {
      await api<AppealView>(`/v2/review/appeals/${appeal.id}/decision`, {
        method: "POST", headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ decision, expectedVersion: appeal.version, ...(decision === "REJECT" ? { reasonCode, reason } : {}) }),
      });
      setSuccess(decision === "ACCEPT" ? "申诉已接受，只开启新的双人审核周期，未直接通过身份。" : "申诉已驳回并保留完整事实记录。");
      setReason(""); await Promise.all([loadAppeals(), loadCases(true)]);
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法处理申诉。")); }
    finally { setBusy(null); }
  };

  const configureCalendar = async (mode: "day" | "hours"): Promise<void> => {
    if (!calendar) return;
    setBusy(`calendar-${mode}`); setError(null); setSuccess("");
    try {
      const path = mode === "day" ? `/v2/review/business-calendar/days/${calendarDate}` : "/v2/review/business-calendar/hours";
      await api<BusinessCalendar>(path, { method: "PUT", body: JSON.stringify(mode === "day"
        ? { dayType: calendarDayType, name: calendarDayName, expectedVersion: calendar.version }
        : { workdayStart, workdayEnd, expectedVersion: calendar.version }) });
      setSuccess("工作日历已按乐观版本更新，变更事实和审计事件已追加保存。");
      setCalendarDate(""); setCalendarDayName(""); await loadCalendar();
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法配置工作日历。")); }
    finally { setBusy(null); }
  };

  const verifyMfaResetIdentity = async (item: MfaResetRequest, decision: "VERIFY" | "REJECT"): Promise<void> => {
    setBusy(`mfa-reset:${decision}:${item.id}`); setError(null); setSuccess("");
    try {
      await api(`/v2/review/mfa-reset-requests/${item.id}/identity-decision`, { method: "POST", body: JSON.stringify({ decision, expectedVersion: item.version }) });
      setSuccess(decision === "VERIFY" ? "目标账号身份已独立复核；下一步仍需两名安全管理员和真实通知门禁。" : "高风险账号恢复申请已驳回。");
      await loadMfaResets();
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法复核 MFA 恢复身份。")); }
    finally { setBusy(null); }
  };

  const logout = async (): Promise<void> => {
    try {
      await api<void>("/v2/auth/session", { method: "DELETE" });
    } finally {
      onLogout();
    }
  };

  return (
    <div className="workspace review-workspace">
      <header className="workspace-header">
        <div className="brand onboarding-brand"><BrandMark /><span><b>链脉信枢</b><small>IDENTITY REVIEW DESK</small></span></div>
        <div className="workspace-actions">
          <span className="environment"><ShieldCheck />{me.platformRoles.includes("PLATFORM_REVIEW_ADMIN") ? "审核管理员" : "平台审核员"}</span>
          {me.trustedContext && <button className="secondary-button" onClick={onOpenEvidence}><FileCheck2 />存证工作台</button>}
          {me.capabilities.viewPlatformSecurity && <button className="secondary-button" onClick={onOpenSecurity}><ShieldCheck />平台安全</button>}
          <button className="secondary-button" onClick={onBack}><ChevronLeft />身份中心</button>
          <button className="icon-button" aria-label="退出登录" onClick={() => void logout()}><LogOut /></button>
        </div>
      </header>

      <main className="workspace-main review-main">
        <section className="welcome-band review-welcome">
          <div><span className="eyebrow"><BadgeCheck />审核权与租户业务权分离</span><h1>可信身份审核工作台</h1><p>审核员不能查看或处理自己的申请；敏感字段解密、材料调阅和审批都进入 MySQL 追加式审计链。</p></div>
          <button className="secondary-button" disabled={loading} onClick={() => void loadCases()}>{loading ? <LoaderCircle className="spin" /> : <RefreshCw />}刷新队列</button>
        </section>

        <section className="boundary-banner">
          <span className="boundary-icon"><ShieldCheck /></span>
          <div><b>受控调阅边界</b><p>材料原文只在审核员明确点击时解密，不会写入响应 JSON、链上 Manifest 或日志；当前为本地信封加密适配器，尚非 HSM 生产保证。</p></div>
          <span className="truth-label">NO SELF REVIEW</span>
        </section>

        {error && <ErrorNotice error={error} />}
        {success && <div className="success-notice"><Check /><span><b>事务已提交</b><small>{success}</small></span></div>}

        <section className="identity-security-grid review-stats">
          <article><span><Inbox /></span><div><small>当前结果</small><strong>{counts.total}</strong><em>已按筛选条件读取</em></div></article>
          <article><span className="amber"><Clock3 /></span><div><small>待领取</small><strong>{counts.unclaimed}</strong><em>不含自己申请</em></div></article>
          <article><span className="violet"><BadgeCheck /></span><div><small>由我审核</small><strong>{counts.mine}</strong><em>已服务端锁定经办人</em></div></article>
          <article><span className="green"><ShieldCheck /></span><div><small>待独立复核</small><strong>{counts.recheck}</strong><em>复核前不开通业务权</em></div></article>
          <article><span className="amber"><Clock3 /></span><div><small>SLA 超时</small><strong>{counts.overdue}</strong><em>使用 MySQL UTC 时钟</em></div></article>
        </section>

        <section className="review-layout">
          <article className="panel review-queue-panel">
            <div className="panel-header"><div><span className="heading-icon"><Inbox /></span><span><h2>审核队列</h2><p>优先级与提交时间稳定排序</p></span></div></div>
            <div className="review-filters">
              <label>状态<select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="ALL">全部</option><option value="SUBMITTED">待领取</option><option value="UNDER_REVIEW">审核中</option><option value="PENDING_RECHECK">待独立复核</option><option value="APPROVED">已通过</option><option value="REJECTED">已驳回</option></select></label>
              <label>类型<select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as typeof typeFilter)}><option value="ALL">全部</option><option value="PERSONAL">个人</option><option value="ORGANIZATION">企业</option></select></label>
            </div>
            <div className="review-case-list">
              {loading && <div className="empty compact-empty"><LoaderCircle className="spin" /><p>正在读取队列…</p></div>}
              {!loading && cases.length === 0 && <div className="empty compact-empty"><Inbox /><p>当前筛选下没有可处理案件。</p></div>}
              {cases.map((item) => (
                <button key={item.id} className={item.id === selectedId ? "selected" : ""} onClick={() => void choose(item.id)}>
                  <span className="review-type-icon">{item.caseType === "PERSONAL" ? <UserRoundCheck /> : <Building2 />}</span>
                  <span><b>{item.subjectLabel}</b><small>{item.applicationNo ?? shortId(item.id)} · {formatTime(item.submittedAt)}</small><em>{item.slaState === "OVERDUE" ? "SLA 已超时" : item.materialRequirementsMet ? "材料齐全" : `需 ${item.materialRequirements.join("、")}`}</em></span>
                  <i className={`review-status ${item.status.toLowerCase()}`}>{statusName(item.status)}</i>
                </button>
              ))}
            </div>
          </article>

          <article className="panel review-detail-panel">
            {!detail ? <div className="empty"><FileCheck2 /><h3>选择一个审核案件</h3><p>详情页的敏感字段调阅会被审计。</p></div> : (
              <>
                <div className="panel-header">
                  <div><span className="heading-icon">{detail.caseType === "PERSONAL" ? <UserRoundCheck /> : <Building2 />}</span><span><h2>{detail.subjectLabel}</h2><p>{detail.applicationNo ?? detail.id}</p></span></div>
                  <span className={`status ${detail.status === "APPROVED" ? "status-received" : detail.status === "REJECTED" ? "status-warning" : "status-draft"}`}>{statusName(detail.status)}</span>
                </div>
                {busy === "detail" ? <div className="empty compact-empty"><LoaderCircle className="spin" /><p>正在受控解密详情…</p></div> : (
                  <div className="review-detail-body">
                    <div className="sensitive-fields">
                      {detail.personal && <><span><small>真实姓名</small><b>{detail.personal.realName}</b></span><span><small>证件类型</small><b>{detail.personal.documentType}</b></span><span className="wide"><small>证件号码</small><code>{detail.personal.documentNumber}</code></span></>}
                      {detail.organization && <><span className="wide"><small>企业法定名称</small><b>{detail.organization.legalName}</b></span><span><small>申请人身份</small><b>{detail.organization.applicantAuthority}</b></span><span><small>统一社会信用代码</small><code>{detail.organization.unifiedSocialCreditCode}</code></span><span className="wide"><small>拟使用场景</small><p>{detail.organization.intendedUsage}</p></span></>}
                    </div>

                    <div className="review-material-section">
                      <div><span><FileText /></span><span><b>认证材料</b><small>{detail.materialRequirementsMet ? "必需类型已齐全" : `缺少：${detail.materialRequirements.join("、")}`}</small></span></div>
                      {detail.materials.length === 0 ? <p className="muted-copy">申请人尚未上传材料，不能审核通过。</p> : detail.materials.map((material) => (
                        <div className="review-material-row" key={material.id}>
                          <FileText />
                          <span><b>{materialName(material.materialType)}</b><small>{material.fileName} · {(material.byteSize / 1024).toFixed(1)} KiB · {material.scannerAssurance}</small><code>{shortId(material.plaintextDigest, 8)}</code></span>
                          <a className="icon-button" aria-label={`下载${material.fileName}`} href={`/v2/review/materials/${material.id}/content`} target="_blank" rel="noreferrer"><Download /></a>
                        </div>
                      ))}
                    </div>

                    <div className="review-case-meta"><span><small>审核周期</small><b>#{detail.reviewCycle} · {detail.reviewPolicy === "DUAL_REVIEW" ? "双人复核" : "历史单审"}</b></span><span><small>版本</small><b>v{detail.version}</b></span><span><small>SLA 截止</small><b>{formatTime(detail.slaDueAt)}</b></span><span><small>经办状态</small><b>{detail.assignedToMe ? "由我经办" : detail.assignedReviewerAccountId ? "已被领取" : detail.status === "PENDING_RECHECK" ? "等待独立复核" : "未领取"}</b></span></div>
                    {timeline.length > 0 && <div className="review-timeline"><b>审核时间线</b>{timeline.map((event) => <div key={event.id}><span>#{event.reviewCycle} · {event.eventType}</span><small>{event.actorAccountId ? shortId(event.actorAccountId, 5) : "SYSTEM"} · {event.reasonCode ?? "无原因代码"} · {formatTime(event.occurredAt)}</small></div>)}</div>}

                    {detail.status === "SUBMITTED" && <button className="primary-button" disabled={Boolean(busy)} onClick={() => void claim()}>{busy === "claim" ? <LoaderCircle className="spin" /> : <BadgeCheck />}领取审核案件</button>}
                    {detail.status === "UNDER_REVIEW" && detail.assignedToMe && (
                      <div className="review-decision-box">
                        <div className="approval-boundary"><ShieldCheck /><span><b>{detail.reviewPolicy === "DUAL_REVIEW" ? "初审只提交建议" : "历史单审兼容"}</b><small>{detail.reviewPolicy === "DUAL_REVIEW" ? "建议提交后进入独立复核，此时不创建 Tenant、不授予业务角色。" : "仅适用于 v0.6 历史案件。"}</small></span></div>
                        <div className="rejection-fields"><label>驳回原因代码<input value={reasonCode} maxLength={64} onChange={(event) => setReasonCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_:-]/gu, ""))} /></label><label>驳回说明<textarea value={reason} minLength={2} maxLength={1000} onChange={(event) => setReason(event.target.value)} placeholder="仅在驳回时必填，将以密文保存" /></label></div>
                        <div className="review-decision-actions">
                          <button className="primary-button" disabled={Boolean(busy) || !detail.materialRequirementsMet} onClick={() => void decide("APPROVE")}>{busy === "APPROVE" ? <LoaderCircle className="spin" /> : <Check />}{detail.reviewPolicy === "DUAL_REVIEW" ? "建议通过" : "审核通过"}</button>
                          <button className="secondary-button danger-button" disabled={Boolean(busy) || reason.trim().length < 2 || !reasonCode} onClick={() => void decide("REJECT")}>{busy === "REJECT" ? <LoaderCircle className="spin" /> : <X />}{detail.reviewPolicy === "DUAL_REVIEW" ? "建议驳回" : "驳回申请"}</button>
                          {detail.reviewPolicy === "DUAL_REVIEW" && <button className="secondary-button" disabled={Boolean(busy) || reason.trim().length < 2 || !reasonCode} onClick={() => void releaseOrReassign("release")}>退回队列</button>}
                        </div>
                        {!detail.materialRequirementsMet && <small className="decision-hint"><CircleAlert />必需材料不齐，服务端也会拒绝通过决定。</small>}
                      </div>
                    )}
                    {detail.status === "UNDER_REVIEW" && detail.reviewPolicy === "DUAL_REVIEW" && me.platformRoles.includes("PLATFORM_REVIEW_ADMIN") && (
                      <div className="review-decision-box"><div className="approval-boundary"><BadgeCheck /><span><b>指定转派</b><small>新经办人必须为活跃审核员，转派后旧经办人立即失权。</small></span></div><div className="rejection-fields"><label>新经办人<select value={targetReviewerId} onChange={(event) => setTargetReviewerId(event.target.value)}><option value="">请选择</option>{reviewers.filter((item) => item.accountId !== detail.assignedReviewerAccountId && item.accountId !== detail.submittedByAccountId).map((item) => <option value={item.accountId} key={item.accountId}>{item.maskedEmail} · {shortId(item.accountId, 5)}</option>)}</select></label></div><button className="secondary-button" disabled={Boolean(busy) || !targetReviewerId || !reasonCode || reason.trim().length < 2} onClick={() => void releaseOrReassign("reassign")}>确认转派</button></div>
                    )}
                    {detail.status === "PENDING_RECHECK" && detail.recheckStatus === "PENDING" && <button className="primary-button" disabled={Boolean(busy)} onClick={() => void claimRecheck()}>{busy === "claim-recheck" ? <LoaderCircle className="spin" /> : <ShieldCheck />}领取独立复核</button>}
                    {detail.status === "PENDING_RECHECK" && detail.recheckStatus === "CLAIMED" && detail.assignedToMe && (
                      <div className="review-decision-box"><div className="approval-boundary"><ShieldCheck /><span><b>独立复核门禁</b><small>申请人、初审员及本周期经办人均不得复核；服务端会再次强制校验。</small></span></div><div className="rejection-fields"><label>退回原因代码<input value={reasonCode} maxLength={64} onChange={(event) => setReasonCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_:-]/gu, ""))} /></label><label>退回说明<textarea value={reason} minLength={2} maxLength={1000} onChange={(event) => setReason(event.target.value)} /></label></div><div className="review-decision-actions"><button className="primary-button" disabled={Boolean(busy)} onClick={() => void recheck("CONFIRM")}>{busy === "CONFIRM" ? <LoaderCircle className="spin" /> : <Check />}确认初审建议</button><button className="secondary-button danger-button" disabled={Boolean(busy) || !reasonCode || reason.trim().length < 2} onClick={() => void recheck("RETURN_FOR_REVIEW")}>{busy === "RETURN_FOR_REVIEW" ? <LoaderCircle className="spin" /> : <X />}退回新周期</button></div></div>
                    )}
                  </div>
                )}
              </>
            )}
          </article>
        </section>

        <section className="panel">
          <div className="panel-header"><div><span className="heading-icon"><GavelIcon /></span><span><h2>认证驳回申诉</h2><p>原申请人、原初审员、原复核员均不得处理；接受申诉仅开启新审核周期。</p></span></div><button className="secondary-button" onClick={() => void loadAppeals()}><RefreshCw />刷新申诉</button></div>
          {appeals.length === 0 ? <div className="empty compact-empty"><Inbox /><p>当前没有申诉任务。</p></div> : <div className="security-request-list">{appeals.map((appeal) => <article key={appeal.id}>
            <div className="security-request-head"><span><b>案件 {shortId(appeal.reviewCaseId)}</b><small>原审核周期 #{appeal.appealedReviewCycle} · {formatTime(appeal.submittedAt)}</small></span><i className={`review-status ${appeal.status.toLowerCase()}`}>{appeal.status}</i></div>
            {appeal.canCurrentActorClaim && <button className="primary-button" disabled={Boolean(busy)} onClick={() => void claimAppeal(appeal)}>{busy === `appeal-claim:${appeal.id}` ? <LoaderCircle className="spin" /> : <BadgeCheck />}领取申诉</button>}
            {appeal.canCurrentActorDecide && <div className="security-decision-box"><div className="rejection-fields"><label>驳回原因代码<input value={reasonCode} onChange={(event) => setReasonCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_:-]/gu, ""))} /></label><label>驳回说明<textarea value={reason} onChange={(event) => setReason(event.target.value)} /></label></div><div><button className="primary-button" disabled={Boolean(busy)} onClick={() => void decideAppeal(appeal, "ACCEPT")}><Check />接受并开启新周期</button><button className="secondary-button danger-button" disabled={Boolean(busy) || !reasonCode || reason.trim().length < 2} onClick={() => void decideAppeal(appeal, "REJECT")}><X />驳回申诉</button></div></div>}
          </article>)}</div>}
        </section>

        <section className="panel">
          <div className="panel-header"><div><span className="heading-icon"><ShieldCheck /></span><span><h2>高风险账号恢复身份复核</h2><p>只确认申请人身份，不直接关闭 MFA；目标账号本人不能自审。</p></span></div><button className="secondary-button" onClick={() => void loadMfaResets()}><RefreshCw />刷新</button></div>
          {mfaResets.length === 0 ? <div className="empty compact-empty"><ShieldCheck /><p>当前没有 MFA 恢复申请。</p></div> : <div className="security-request-list">{mfaResets.map((item) => <article key={item.id}><div className="security-request-head"><span><b>账号 {shortId(item.targetAccountId)}</b><small>申请 {formatTime(item.requestedAt)} · 版本 {item.version}</small></span><i className={`review-status ${item.status.toLowerCase()}`}>{item.status}</i></div>{item.canCurrentActorVerifyIdentity && <div className="security-decision-box"><div><button className="primary-button" disabled={Boolean(busy)} onClick={() => void verifyMfaResetIdentity(item, "VERIFY")}><Check />确认身份</button><button className="secondary-button danger-button" disabled={Boolean(busy)} onClick={() => void verifyMfaResetIdentity(item, "REJECT")}><X />驳回</button></div></div>}</article>)}</div>}
        </section>

        {calendar && me.platformRoles.includes("PLATFORM_REVIEW_ADMIN") && <section className="panel">
          <div className="panel-header"><div><span className="heading-icon"><Clock3 /></span><span><h2>严格工作小时日历</h2><p>{calendar.name} · {calendar.timeZone} · 当前版本 v{calendar.version}</p></span></div></div>
          <div className="security-form-grid"><label>工作开始<input type="time" value={workdayStart} onChange={(event) => setWorkdayStart(event.target.value)} /></label><label>工作结束<input type="time" value={workdayEnd} onChange={(event) => setWorkdayEnd(event.target.value)} /></label><button className="secondary-button" disabled={Boolean(busy) || workdayStart >= workdayEnd} onClick={() => void configureCalendar("hours")}>更新工作时段</button><label>覆盖日期<input type="date" value={calendarDate} onChange={(event) => setCalendarDate(event.target.value)} /></label><label>日期类型<select value={calendarDayType} onChange={(event) => setCalendarDayType(event.target.value as typeof calendarDayType)}><option value="HOLIDAY">法定节假日</option><option value="WORKDAY">调休补班日</option></select></label><label>日期名称<input value={calendarDayName} maxLength={190} onChange={(event) => setCalendarDayName(event.target.value)} placeholder="例如：国庆节调休" /></label><button className="secondary-button" disabled={Boolean(busy) || !calendarDate || !calendarDayName.trim()} onClick={() => void configureCalendar("day")}>保存日期覆盖</button></div>
          <small className="decision-hint"><ShieldCheck />2026 年已按国务院办公厅通知预置；周末补班日会按工作日计算，法定连休日不会消耗 4 小时 SLA。</small>
        </section>}

        <footer className="workspace-footer"><span>链脉信枢 · 身份运营与生产安全门禁 v0.7 D09＋D10</span><span>lianmai.net.cn · 京ICP备2026024994号-1</span></footer>
      </main>
    </div>
  );
}

function GavelIcon(): React.JSX.Element { return <ShieldCheck />; }
